import type {
  AuthResponse, CharacterSummary, CharacterView, ChronicleEntry, Command, FoundHouseRequest, HouseSummary,
  HouseView, LoginRequest, MarriageCandidatesResponse, MeResponse, ServiceCandidatesResponse, SquireCandidatesResponse, QueuedCommand, RegisterRequest, TitleRank, TitleView, WorldView, CitiesView, CompaniesView, DeliveriesView, FrontierView, GuildView, LoansView, MarketView,
} from '@pk/shared';
import type {
  CharacterHistoryResponse, ChatChannelsResponse, ChatMessage, ChatMessagesResponse, DmTarget,
  HistoryCharacter, PedigreeResponse, TitleHistoryResponse,
} from '@pk/shared';
import { getToken, setToken } from './token';
import { mockRequest } from './mock';

/**
 * Where the API is.
 *
 * Unset in a BUILT bundle means the page's own origin: in production the game
 * server serves this client, so `/api` and `/ws` are on the same host — and a
 * bundle with a host compiled into it would have to be rebuilt for every
 * deployment URL. Unset while developing (`vite`) still means the local server
 * on 8787. `VITE_API_URL` overrides both, for a client hosted apart from its
 * server. An empty value is what `ws.ts` already treats as "same origin".
 */
export const API_URL: string = (import.meta.env.VITE_API_URL ?? (import.meta.env.DEV ? 'http://localhost:8787' : '')).replace(/\/$/, '');
export const MOCK: boolean = import.meta.env.VITE_MOCK === '1' || import.meta.env.VITE_MOCK === 'true';

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

/**
 * Copy the parsed error body's extra fields (e.g. chat 403s carry mutedUntilMs/bannedUntilMs)
 * onto the thrown ApiError so screens can react to them. `message`/`status` are kept: the
 * ApiError's own fields win, so every other route still sees the usual error text.
 */
function attachExtras(err: ApiError, data: unknown): ApiError {
  if (!data || typeof data !== 'object' || Array.isArray(data)) return err;
  for (const [k, v] of Object.entries(data as Record<string, unknown>)) {
    if (k === 'message' || k === 'status') continue;
    (err as unknown as Record<string, unknown>)[k] = v;
  }
  return err;
}

async function request<T>(method: 'GET' | 'POST' | 'DELETE', path: string, body?: unknown): Promise<T> {
  if (MOCK) return (await mockRequest(method, path, body)) as T;
  const headers: Record<string, string> = {};
  const token = getToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  let res: Response;
  try {
    res = await fetch(`${API_URL}${path}`, { method, headers, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, 'network');
  }
  if (res.status === 401 && token && !path.startsWith('/api/auth/')) {
    await setToken(null);
  }
  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if (!res.ok) {
    const d = data as { error?: string; message?: string } | string | null;
    const msg = typeof d === 'string' ? d : d?.error ?? d?.message ?? `HTTP ${res.status}`;
    throw attachExtras(new ApiError(res.status, msg), data);
  }
  return data as T;
}

const q = (params: Record<string, string | number | boolean | undefined>) => {
  const s = Object.entries(params)
    .filter(([, v]) => v !== undefined)
    .map(([k, v]) => `${k}=${encodeURIComponent(String(v))}`)
    .join('&');
  return s ? `?${s}` : '';
};

export type HouseDetail = HouseSummary & { members: CharacterSummary[] };

export const api = {
  register: (b: RegisterRequest) => request<AuthResponse>('POST', '/api/auth/register', b),
  login: (b: LoginRequest) => request<AuthResponse>('POST', '/api/auth/login', b),
  codaiUrl: () => `${API_URL}/api/auth/codai`,
  me: () => request<MeResponse>('GET', '/api/me'),
  foundHouse: (b: FoundHouseRequest) => request<MeResponse>('POST', '/api/house/found', b),
  heartbeat: () => request<{ ok: true }>('POST', '/api/heartbeat'),
  world: () => request<WorldView>('GET', '/api/world'),
  house: () => request<HouseView>('GET', '/api/house'),
  character: (id: string) => request<CharacterView>('GET', `/api/characters/${encodeURIComponent(id)}`),
  title: (id: string) => request<TitleView>('GET', `/api/titles/${encodeURIComponent(id)}`),
  houseDetail: (id: string) => request<HouseDetail>('GET', `/api/houses/${encodeURIComponent(id)}`),
  chronicle: (opts: { before?: number; limit?: number; mine?: boolean }) =>
    request<ChronicleEntry[]>('GET', `/api/chronicle${q({ before: opts.before, limit: opts.limit ?? 50, mine: opts.mine ? 1 : undefined })}`),
  postCommand: (command: Command) => request<QueuedCommand>('POST', '/api/commands', { command }),
  getCommand: (id: string) => request<QueuedCommand>('GET', `/api/commands/${encodeURIComponent(id)}`),
  getPedigree: (houseId: string) => request<PedigreeResponse>('GET', `/api/houses/${encodeURIComponent(houseId)}/pedigree`),
  getTitleHistory: (titleId: string) => request<TitleHistoryResponse>('GET', `/api/titles/${encodeURIComponent(titleId)}/history`),
  getCharacterHistory: (characterId: string) => request<CharacterHistoryResponse>('GET', `/api/characters/${encodeURIComponent(characterId)}/history`),
  searchCharacters: (params: { q: string; alive?: boolean; houseId?: string; rank?: TitleRank; limit?: number }) =>
    request<HistoryCharacter[]>('GET', `/api/search/characters${q({
      q: params.q, alive: params.alive === undefined ? undefined : params.alive ? 1 : 0,
      houseId: params.houseId, rank: params.rank, limit: params.limit ?? 25,
    })}`),
  getMarriageCandidates: (params: { charId: string; dowry?: number; matrilineal?: boolean; minAge?: number; maxAge?: number; limit?: number }) =>
    request<MarriageCandidatesResponse>('GET', `/api/marriage/candidates${q(params)}`),
  getServiceCandidates: (params: { knightId: string; limit?: number }) =>
    request<ServiceCandidatesResponse>('GET', `/api/service/candidates${q(params)}`),
  getSquireCandidates: (params: { squireId: string; limit?: number }) =>
    request<SquireCandidatesResponse>('GET', `/api/squire/candidates${q(params)}`),
  registerPush: (token: string, platform: string) => request<{ ok: true }>('POST', '/api/push/register', { token, platform }),
  getPushPublicKey: () => request<{ publicKey: string | null }>('GET', '/api/push/public-key'),
  health: () => request<{ ok: true; day: number }>('GET', '/api/health'),
  cities: () => request<CitiesView>('GET', '/api/cities'),
  frontier: () => request<FrontierView>('GET', '/api/frontier'),
  market: (cityId: string) => request<MarketView>('GET', `/api/cities/${encodeURIComponent(cityId)}/market`),
  guild: (cityId: string) => request<GuildView>('GET', `/api/cities/${encodeURIComponent(cityId)}/guild`),
  companies: () => request<CompaniesView>('GET', '/api/companies'),
  loans: () => request<LoansView>('GET', '/api/loans'),
  deliveries: () => request<DeliveriesView>('GET', '/api/deliveries'),

  // ---------------------------------------------------------------- chat
  getChat: () => request<ChatChannelsResponse>('GET', '/api/chat'),
  getChatMessages: (channelId: string, before?: string) =>
    request<ChatMessagesResponse>('GET', `/api/chat/channels/${encodeURIComponent(channelId)}/messages${q({ before, limit: 50 })}`),
  sendChatMessage: (channelId: string, text: string) =>
    request<{ message: ChatMessage }>('POST', `/api/chat/channels/${encodeURIComponent(channelId)}/messages`, { text }),
  readChat: (channelId: string) => request<{ ok: true }>('POST', '/api/chat/read', { channelId }),
  getChatBlocks: () => request<{ blocked: DmTarget[] }>('GET', '/api/chat/blocks'),
  blockUser: (accountId: string) => request<{ ok: true }>('POST', '/api/chat/blocks', { accountId }),
  unblockUser: (accountId: string) => request<{ ok: true }>('DELETE', `/api/chat/blocks/${encodeURIComponent(accountId)}`),
  getDmTargets: (limit = 50) => request<{ targets: DmTarget[] }>('GET', `/api/chat/dm-targets${q({ limit })}`),
  reportChat: (b: { kind: 'message' | 'user'; messageId?: string; accountId?: string; reason: string }) =>
    request<{ ok: true }>('POST', '/api/chat/reports', b),
};
